import { gql } from "@apollo/client";

export const GET_USER_APPS = gql`
  query userApplications($filters: AppFilters) {
    getUserApplications(filters: $filters) {
      language
      appId
      title
      url
      description
      icon
      score
      developer
      store
      size
      updated
      reviewsCount
      country
      version
      updated
      status
      limit
      offset
      query
      reviews {
        text
        score
        userName
      }
    }
  }
`;

export const GET_USER_APPS_SHORTLINK = gql`
  query userAppsShortlink {
    getUserAppsShortlink
  }
`;

export const SEARCH_APPS = gql`
  query searchApps(
    $keyword: String
    $num: Int
    $country: String
    $language: String
  ) {
    searchMarketData(
      keyword: $keyword
      num: $num
      country: $country
      language: $language
    ) {
      playStoreSearchResults {
        title
        url
        appId
        icon
        developer
        store
        genres
        price
        contentRating
        geoFromLink
      }
      appStoreSearchResults {
        id
        appId
        title
        url
        icon
        developer
        genres
        price
        contentRating
        store
        geoFromLink
      }
    }
  }
`;

export const GET_SUGGESTED_KEYWORDS = gql`
  query GetSuggestions(
    $searchTerm: [String!]
    $country: String
    $language: String!
  ) {
    getSuggestions(
      searchTerm: $searchTerm
      country: $country
      language: $language
    )
  }
`;

export const ADD_APPLICATION = gql`
  mutation addApp($appId: String!, $store: Store!, $options: Options!) {
    addApplication(appId: $appId, store: $store, options: $options) {
      title
      statusCode
      error
      internalId
    }
  }
`;

export const CHECK_APP_AVAILABILITY = gql`
  query CheckAppAvailability(
    $appId: String!
    $store: Store!
    $options: Options!
  ) {
    checkAppAvailability(appId: $appId, store: $store, options: $options) {
      statusCode
      error
    }
  }
`;

// Whether the app is on sale in the country, the strict way: a Google Play app
// missing from the country is refused too, not only one gone from the store.
// Asked before adding an app and before a new order (the order pages and the
// planner's geo blocks) — an app not sold in a country gets no installs there.
// Orders already running are checked by the backend's own looser rule.
export const CHECK_APP_SOLD_IN_COUNTRY = gql`
  query CheckAppSoldInCountry(
    $appId: String!
    $store: Store!
    $options: Options!
  ) {
    checkAppAvailability(
      appId: $appId
      store: $store
      options: $options
      forAdding: true
    ) {
      statusCode
      error
    }
  }
`;

export const GET_APP_DATA = gql`
  query getAppData(
    $store: Store!
    $options: GetAppDataInput
    $getLocalInfo: Boolean
    $getReviews: Boolean
  ) {
    getAppData(
      store: $store
      options: $options
      getLocalInfo: $getLocalInfo
      getReviews: $getReviews
    ) {
      id
      internalId
      developer
      appId
      title
      language
      country
      url
      description
      icon
      score
      status
      developer
      store
      size
      updated
      reviewsCount
      version
      init
      reviews {
        one
        two
        three
        four
        five
      }
      reviewsScore
      genre
      free
      price
      contentRating
      defaultCategory
      defaultContentRating
      histogram {
        one
        two
        three
        four
        five
      }
      screenshots
      ipadScreenshots
      releaseNotes
      released
      requiredOsVersion
      languages
    }
  }
`;

export const GET_APP_LISTING = gql`
  query getAppListing(
    $appId: String!
    $store: Store
    $country: String
    $language: String
    $withSubtitles: Boolean
  ) {
    getAppListing(
      appId: $appId
      store: $store
      country: $country
      language: $language
      withSubtitles: $withSubtitles
    ) {
      appId
      subtitle
      genres
      similar {
        appId
        title
        icon
        developer
        score
        ratings
        genre
        subtitle
      }
      moreByDeveloper {
        appId
        title
        icon
        developer
        score
        genre
      }
    }
  }
`;

/**
 * Storefront screenshots: the App Store page of the app rendered in a mobile
 * viewport, one image per country. Slow on a cold cache — every country is a
 * page render — so the screen shows placeholders while it runs.
 */
export const GET_APP_STOREFRONTS = gql`
  query getAppStorefronts(
    $appId: String!
    $store: Store
    $countries: [String]
    $language: String
    $refresh: Boolean
  ) {
    getAppStorefronts(
      appId: $appId
      store: $store
      countries: $countries
      language: $language
      refresh: $refresh
    ) {
      appId
      unavailable
      failed
      shots {
        country
        language
        url
        image
        width
        height
        title
      }
    }
  }
`;

// Where a keyword ranks for the app in every storefront of its store. The
// check runs in the background on the backend: while `status` is "pending"
// the rows fill in and the caller polls.
export const GET_KEYWORD_GEO_RANKS = gql`
  query getKeywordGeoRanks(
    $appId: String!
    $keyword: String!
    $store: Store
    $refresh: Boolean
  ) {
    getKeywordGeoRanks(
      appId: $appId
      keyword: $keyword
      store: $store
      refresh: $refresh
    ) {
      keyword
      status
      done
      total
      ranks {
        country
        available
        rank
        popularity
        difficulty
        tracked
        checkedAt
      }
    }
  }
`;

// Daily rank of a keyword in every country the user tracks it in, from the
// index the keyword refresh writes — no scraper requests behind it.
export const GET_KEYWORD_RANK_HISTORY = gql`
  query getKeywordRankHistory(
    $appId: String!
    $keyword: String!
    $store: Store
    $days: Int
  ) {
    getKeywordRankHistory(
      appId: $appId
      keyword: $keyword
      store: $store
      days: $days
    ) {
      country
      language
      rank
      checkedAt
      popularity
      difficulty
      metricsDate
      points {
        date
        rank
      }
    }
  }
`;

// Live rank, popularity and difficulty for the keyword in every country the
// user tracks it in; saved where the twice-daily refresh writes.
export const REFRESH_KEYWORD_COUNTRY_METRICS = gql`
  mutation refreshKeywordCountryMetrics(
    $appId: String!
    $keyword: String!
    $store: Store
  ) {
    refreshKeywordCountryMetrics(
      appId: $appId
      keyword: $keyword
      store: $store
    ) {
      updated
      failed
    }
  }
`;

export const GET_ASO_RANKED_KEYWORDS = gql`
  query getAsoRankedKeywords(
    $appId: String!
    $store: Store
    $country: String
    $language: String
    $refresh: Boolean
  ) {
    getAsoRankedKeywords(
      appId: $appId
      store: $store
      country: $country
      language: $language
      refresh: $refresh
    ) {
      appId
      country
      status
      updatedAt
      keywords {
        keyword
        position
        rank
        difficulty
        popularity
        date
      }
    }
  }
`;

export const GET_APP_CHART_HISTORY = gql`
  query getAppChartHistory(
    $appId: String!
    $store: Store
    $country: String
    $days: Int
  ) {
    getAppChartHistory(
      appId: $appId
      store: $store
      country: $country
      days: $days
    ) {
      appId
      country
      chartType
      current {
        overall
        category
        genreId
        genreName
      }
      overall {
        position
        capturedAt
      }
      category {
        position
        capturedAt
        genreId
      }
    }
  }
`;

/** The banner above the app's App Store page; null when the app has none. */
export const GET_APP_STORE_BANNER = gql`
  query getAppStoreBanner($appId: String!, $store: Store, $country: String) {
    getAppStoreBanner(appId: $appId, store: $store, country: $country) {
      url
      width
      height
      backgroundColor
      textColor
    }
  }
`;

export const GET_APP_RECENT_REVIEWS = gql`
  query getAppRecentReviews(
    $appId: String!
    $store: Store
    $country: String
    $language: String
    $limit: Int
  ) {
    getAppRecentReviews(
      appId: $appId
      store: $store
      country: $country
      language: $language
      limit: $limit
    ) {
      id
      score
      title
      text
      userName
      date
    }
  }
`;

export const GET_APP_VERSION_HISTORY = gql`
  query getAppVersionHistory(
    $appId: String!
    $store: Store
    $country: String
    $limit: Int
  ) {
    getAppVersionHistory(
      appId: $appId
      store: $store
      country: $country
      limit: $limit
    ) {
      appId
      versions {
        version
        releasedAt
        notes
      }
    }
  }
`;

export const GET_ASO_KEYWORDS_HISTORY = gql`
  query getAsoKeywordsHistory(
    $appId: String!
    $store: Store
    $country: String
    $days: Int
  ) {
    getAsoKeywordsHistory(
      appId: $appId
      store: $store
      country: $country
      days: $days
    ) {
      date
      top1
      top2to5
      top6to10
      top11to20
      top21to100
      top100plus
      total
    }
  }
`;

export const GET_USER_APPS_SHORT_INFO = gql`
  query userApplications($filters: AppFilters) {
    getUserApplications(filters: $filters) {
      appId
      title
      url
      icon
      store
      status
    }
  }
`;

export const GET_RECENT_APPS = gql`
  query getRecentApplications {
    getRecentApplications {
      appId
      title
      icon
      store
      ordersAmount
      country
      language
      status
    }
  }
`;

export const ADD_APP_KEYWORDS_AND_GROUPS = gql`
  mutation addKeywordsToGroup(
    $appId: String!
    $groupName: String!
    $keywords: [String!]!
    $country: String!
    $language: String!
    $store: Store
  ) {
    addKeywordsToGroup(
      appId: $appId
      groupName: $groupName
      keywords: $keywords
      country: $country
      language: $language
      store: $store
    ) {
      answer
    }
  }
`;

export const GET_USER_KEYWORDS_GROUPS = gql`
  query GetUserKeywordsGroups(
    $appId: String!
    $country: String!
    $language: String!
    $store: Store
  ) {
    getUserGroupsKeywords(
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      groupName
      keywords {
        keyword
        isFavorite
        position
        spent
        status
        SecondaryKeywordMetrics {
          popularity
          difficulty
          date
        }
        historicalData {
          date
          index
        }
        installsHistory {
          date
          installs
        }
      }
    }
  }
`;

export const SET_FAVORITE_KEYWORD = gql`
  mutation changeKeywordFavorite(
    $keyword: String
    $appId: String
    $country: String
    $language: String
    $store: Store
  ) {
    changeKeywordFavorite(
      keyword: $keyword
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      answer
    }
  }
`;

export const GET_USER_APPS_SHORT = gql`
  query userApplications($filters: AppFilters) {
    getUserApplications(filters: $filters) {
      internalId
      appId
      country
      title
      url
      icon
      developer
      store
      status
      price
    }
  }
`;

export const GET_MOST_POPULAR_APPS_BY_KEYWORD = gql`
  query getMostPopularAppsByKeyword($options: Options, $store: Store) {
    getMostPopularAppsByKeyword(options: $options, store: $store) {
      appId
      title
      url
      icon
      developer
    }
  }
`;

export const GET_SUGGESTIONS_TO_KEYWORD = gql`
  query getKeywordsSuggestions($store: Store, $options: Options) {
    getKeywordsSuggestions(store: $store, options: $options)
  }
`;

export const DELETE_APP = gql`
  mutation deleteApp($appId: String, $store: Store, $options: Options) {
    deleteApp(appId: $appId, store: $store, options: $options)
  }
`;

export const RENAME_KEYWORDS_GROUP = gql`
  mutation renameKeywordGroup(
    $oldGroupName: String!
    $newGroupName: String!
    $appId: String!
    $country: String
    $language: String
    $store: Store
  ) {
    renameKeywordGroup(
      oldGroupName: $oldGroupName
      newGroupName: $newGroupName
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      groupName
      keywords {
        keyword
        isFavorite
        position
        #        volume
        #        volumeTrend
      }
    }
  }
`;

export const DELETE_KEYWORDS_GROUP = gql`
  mutation deleteKeywordsGroup(
    $groupName: String!
    $appId: String!
    $country: String
    $language: String
    $store: Store
  ) {
    deleteKeywordGroup(
      groupName: $groupName
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      success
      message
    }
  }
`;

export const DELETE_KEYWORDS_FROM_GROUP = gql`
  mutation deleteKeywordsFromGroup(
    $groupName: String!
    $appId: String!
    $keywords: [String]
    $country: String
    $language: String
    $store: Store
  ) {
    deleteKeywordsFromGroup(
      groupName: $groupName
      appId: $appId
      keywords: $keywords
      country: $country
      language: $language
      store: $store
    ) {
      success
      message
    }
  }
`;

export const ADD_KEYWORDS_TO_GROUP = gql`
  mutation addKeywordsToGroup(
    $appId: String!
    $groupName: String!
    $keywords: [String!]!
    $country: String!
    $language: String!
    $store: Store
  ) {
    addKeywordsToGroup(
      appId: $appId
      groupName: $groupName
      keywords: $keywords
      country: $country
      language: $language
      store: $store
    ) {
      answer
    }
  }
`;

export const GET_SECONDARY_KEYWORD_METRICS = gql`
  query getSecondaryKeywordMetrics(
    $term: [String!]!
    $appId: String!
    $country: String!
    $language: String!
    $platform: Store!
  ) {
    getSecondaryKeywordMetrics(
      term: $term
      appId: $appId
      country: $country
      language: $language
      platform: $platform
    ) {
      keyword
      popularity
      difficulty
      date
    }
  }
`;

export const GET_STORE_KEYWORD_POSITION = gql`
  query GetStoreKeywordPosition(
    $term: [String!]!
    $appId: String!
    $country: String!
    $language: String!
    $platform: Store!
  ) {
    getStoreKeywordPosition(
      term: $term
      appId: $appId
      country: $country
      language: $language
      platform: $platform
    ) {
      keyword
      position
      volume
    }
  }
`;

export const GET_SCRAPER_KEYWORD_METRICS = gql`
  query getScraperKeywordMetrics(
    $term: String!
    $appId: String!
    $country: String!
    $language: String
    $store: Store
  ) {
    getScraperKeywordMetrics(
      term: $term
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      keyword
      position
      rank
      difficulty
      popularity
      totalResults
      date
    }
  }
`;

export const GET_SCRAPER_RELEVANT_KEYWORDS = gql`
  query getScraperRelevantKeywords(
    $appId: String!
    $country: String!
    $language: String
    $store: Store
  ) {
    getScraperRelevantKeywords(
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      keyword
      position
      rank
      difficulty
      popularity
      totalResults
      date
    }
  }
`;

export const ADD_SCRAPER_KEYWORDS_TO_GROUP = gql`
  mutation addScraperKeywordsToGroup(
    $appId: String!
    $country: String!
    $language: String
    $store: Store
    $keywords: [String!]!
  ) {
    addScraperKeywordsToGroup(
      appId: $appId
      country: $country
      language: $language
      store: $store
      keywords: $keywords
    ) {
      answer
    }
  }
`;

export const GET_SCRAPER_SERVICES_PRICING = gql`
  query getScraperServicesPricing {
    getScraperServicesPricing {
      keywordMetrics
      relevantKeywords
    }
  }
`;

export const GET_AI_SUGGESTIONS = gql`
  query getAISuggestionsByGeo($options: GPTSuggestionsInput) {
    getAISuggestionsByGeo(options: $options)
  }
`;

export const GET_AI_SUGGESTIONS_WITH_INFO = gql`
  query getAISuggestionsByGeoWithInfo($options: GPTSuggestionsInput) {
    getAISuggestionsByGeoWithInfo(options: $options) {
      keyword
      position
      date
      difficulty
      popularity
    }
  }
`;
export const CHECK_KEYWORDS_INFO = gql`
  query checkKeywordsPositions(
    $options: GPTSuggestionsInput
    $keywords: [String]
  ) {
    checkKeywordsPositions(options: $options, keywords: $keywords) {
      keyword
      position
      date
      difficulty
      popularity
    }
  }
`;
export interface KeywordMetrics {
  difficulty: number;
  popularity: number;
  date: string;
}

export interface AppSearchIndexData {
  dateOfSearch: string;
  time: string;
  index: string;
  appId: number;
}

export interface KeywordInput {
  keyword: string;
  language: string;
  country: string;
  SecondaryKeywordMetrics: KeywordMetrics;
  AppSearchIndex: AppSearchIndexData;
  groupName: string;
}

export interface KeywordCreateInput {
  keywords: KeywordInput[];
}

export interface Options {
  appId: string;
  country: string;
  language: string;
}

export interface KeywordGroupInput {
  appId: string;
  country: string;
  language: string;
}

export const GET_APP_FAVOURITE_COUNTRIES = gql`
  query getAppFavouriteCountries($appId: String, $store: Store) {
    getAppFavouriteCountries(appId: $appId, store: $store)
  }
`;

export const GET_REVIEW_APP_STORE = gql`
  query getReviewsAppStore(
    $appId: String
    $country: String
    $language: String
  ) {
    getReviewsAppStore(appId: $appId, country: $country, language: $language) {
      reviews {
        one
        two
        three
        four
        five
      }
      reviewsScore
    }
  }
`;

export const GET_REVIEW_PLAY_STORE = gql`
  query getReviewsPlayStore(
    $appId: String
    $country: String
    $language: String
  ) {
    getReviewsPlayStore(appId: $appId, country: $country, language: $language) {
      reviews {
        one
        two
        three
        four
        five
      }
      reviewsScore
    }
  }
`;

export const GET_REVIEW_STORE = gql`
  query getReviewsStore(
    $appId: String
    $country: String
    $language: String
    $store: String
  ) {
    getReviewsStore(
      appId: $appId
      country: $country
      language: $language
      store: $store
    ) {
      reviews {
        one
        two
        three
        four
        five
      }
      reviewsScore
    }
  }
`;

export const GET_RATING_APP_STORE = gql`
  query getRatingAppStore($appId: String, $country: String, $language: String) {
    getRatingAppStore(appId: $appId, country: $country, language: $language) {
      histogram {
        one
        two
        three
        four
        five
      }
      score
    }
  }
`;

export const GET_USER_CONNECTED_APPS = gql`
  query userConnectedApps($filters: AppFilters) {
    getUserConnectedApps(filters: $filters) {
      language
      appId
      title
      url
      description
      icon
      score
      developer
      store
      size
      updated
      reviewsCount
      country
      version
      updated
      status
      reviews {
        text
        score
        userName
      }
    }
  }
`;

export const GET_APP_DB_DATA = gql`
  query getAppDbData($appId: String!, $store: Store!) {
    getAppDbData(appId: $appId, store: $store) {
      init
    }
  }
`;

/** The language the app was added with — App Insights keeps its keywords in it. */
export const GET_APP_KEYWORDS_LANGUAGE = gql`
  query getAppKeywordsLanguage($appId: String!, $store: Store!) {
    getAppDbData(appId: $appId, store: $store) {
      language
    }
  }
`;

export const GET_USER_APPS_LIST = gql`
  query userApplicationsList(
    $filters: AppFilters
    $limit: Float
    $offset: Float
    $query: String!
  ) {
    getUserApplicationsList(
      filters: $filters
      limit: $limit
      offset: $offset
      query: $query
    ) {
      language
      appId
      title
      icon
      score
      store
      size
      updated
      reviewsCount
      country
      version
      status
      sortingKey
    }
  }
`;

export const GET_USER_APPS_LIST_FAST = gql`
  query userApplicationsListFast(
    $filters: AppFilters
    $limit: Float
    $offset: Float
    $query: String!
  ) {
    getUserApplicationsListFast(
      filters: $filters
      limit: $limit
      offset: $offset
      query: $query
    ) {
      language
      appId
      title
      icon
      score
      store
      size
      updated
      reviewsCount
      country
      version
      status
      keywordsCount
      totalSpend
      activeOrdersCount
    }
  }
`;
